import { Mark, mergeAttributes } from '@tiptap/core';

export type FontSizePreset = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const sizeMap: Record<FontSizePreset, string> = {
    sm: '0.875rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '1.5rem',
    '2xl': '2rem',
};

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        fontSize: {
            /** Set a named font-size preset on the current selection. */
            setFontSize: (preset: FontSizePreset) => ReturnType;
            /** Remove the font-size mark from the current selection. */
            unsetFontSize: () => ReturnType;
        };
    }
}

export const FontSizeExtension = Mark.create({
    name: 'fontSize',

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },

    addAttributes() {
        return {
            preset: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-font-size'),
                renderHTML: (attributes) => {
                    if (!attributes.preset) {
                        return {};
                    }

                    return {
                        'data-font-size': attributes.preset,
                        style: `font-size: ${sizeMap[attributes.preset as FontSizePreset] ?? '1rem'}`,
                    };
                },
            },
        };
    },

    parseHTML() {
        return [{ tag: 'span[data-font-size]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
            0,
        ];
    },

    addCommands() {
        return {
            setFontSize:
                (preset: FontSizePreset) =>
                ({ commands }) => {
                    return commands.setMark(this.name, { preset });
                },
            unsetFontSize:
                () =>
                ({ commands }) => {
                    return commands.unsetMark(this.name);
                },
        };
    },
});
